import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FlaskConical, Microscope, Award, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ResearchPage() {
  const [research, setResearch] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResearch();
  }, []);

  const fetchResearch = async () => {
    try {
      const response = await fetch(`${API_URL}/api/research`);
      if (response.ok) {
        const data = await response.json();
        setResearch(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main id="main-content" className="py-8 md:py-12">
        <div className="container-cish">
          {/* Page Header */}
          <div className="text-center mb-8 md:mb-12">
            <div className="inline-flex items-center gap-2 mb-3">
              <Microscope className="w-6 h-6 text-[#0E6D2B]" />
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#0E6D2B]">
                Research
              </h1>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
              Cutting-edge research in subtropical horticulture
            </p>
          </div>

          {/* Research Areas */}
          <div className="mb-12">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-[#0E6D2B]" />
              Key Research Areas
            </h2>
            {loading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-4 border-[#0E6D2B] border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {research.map((item, index) => (
                  <div
                    key={item.id || index}
                    className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow"
                  >
                    <div className="w-12 h-12 rounded-lg bg-[#F4F8EF] flex items-center justify-center mb-4">
                      <FlaskConical className="w-6 h-6 text-[#0E6D2B]" />
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2 text-sm md:text-base line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-sm line-clamp-3">
                      {item.description || 'Research focused on improving subtropical fruit production.'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Achievements Section */}
          <div id="achievements" className="mb-12">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <Award className="w-5 h-5 text-[#0E6D2B]" />
              Research Achievements
            </h2>
            <div className="bg-[#F4F8EF] rounded-xl p-6">
              <ul className="space-y-3 text-gray-700 text-sm md:text-base">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-[#0E6D2B] mt-2 shrink-0"></span>
                  Development of export-quality mango hybrids - Ambika and Arunika
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-[#0E6D2B] mt-2 shrink-0"></span>
                  World's largest mango germplasm collection (755+ accessions)
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-[#0E6D2B] mt-2 shrink-0"></span>
                  Climate-resilient varieties for sustainable production
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-[#0E6D2B] mt-2 shrink-0"></span>
                  Integrated pest management protocols for major subtropical fruits
                </li>
              </ul>
            </div>
          </div>

          {/* Projects Section */}
          <div id="projects">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#0E6D2B]" />
              Ongoing Projects
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Development of climate-smart mango production systems',
                'Biofortification of subtropical fruits',
                'Digital tools for precision horticulture',
                'Value chain development for minor fruits',
              ].map((project, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-[#0E6D2B] transition-colors">
                  <p className="text-gray-700 text-sm md:text-base">{project}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
